import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, CircularProgress, keyframes } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CloseIcon from '@mui/icons-material/Close';
import { SmartInput } from '../components/SmartInput';
import type { SmartInputMode } from '../components/SmartInput';
import { api } from '../services/api';
import { auth } from '../services/auth';
import { useConfigStore } from '../stores/useConfigStore';
// Logo from public/ directory
const logoSvg = process.env.PUBLIC_URL + '/logo.svg';

// ============================================================================
// Constants
// ============================================================================

const INDUSTRIES = [
  'SaaS', 'E-commerce', 'Fintech', 'Healthcare', 'EdTech', 'Logistics',
  'Real Estate', 'Marketing', 'HR Tech', 'Legal Tech', 'InsurTech',
  'AgriTech', 'Travel', 'Gaming', 'Media', 'Telecommunications',
  'Cybersecurity', 'AI / Machine Learning', 'IoT', 'CleanTech',
  'Construction', 'Automotive', 'Food & Beverage', 'Retail',
  'Manufacturing', 'Consulting', 'Other',
];

const COUNTRIES = [
  'United States', 'United Kingdom', 'Canada', 'Australia', 'Germany',
  'France', 'Netherlands', 'Sweden', 'Norway', 'Denmark', 'Finland',
  'Ireland', 'Switzerland', 'Austria', 'Belgium', 'Spain', 'Italy',
  'Portugal', 'Poland', 'Czech Republic', 'Romania', 'Hungary',
  'Croatia', 'Bulgaria', 'Greece', 'Turkey', 'Israel', 'United Arab Emirates',
  'Saudi Arabia', 'India', 'Japan', 'South Korea', 'Singapore',
  'Hong Kong', 'Taiwan', 'China', 'Thailand', 'Vietnam', 'Indonesia',
  'Philippines', 'Malaysia', 'New Zealand', 'South Africa', 'Nigeria',
  'Kenya', 'Egypt', 'Brazil', 'Mexico', 'Argentina', 'Colombia',
  'Chile', 'Peru', 'Costa Rica', 'Panama', 'Uruguay', 'Ecuador',
  'Dominican Republic', 'Puerto Rico', 'Jamaica', 'Trinidad and Tobago',
  'Guatemala', 'El Salvador', 'Honduras', 'Nicaragua', 'Bolivia',
  'Paraguay', 'Venezuela', 'Cuba', 'Haiti', 'Bahamas',
  'Russia', 'Ukraine', 'Kazakhstan', 'Georgia', 'Armenia',
  'Azerbaijan', 'Uzbekistan', 'Belarus', 'Moldova', 'Latvia',
  'Lithuania', 'Estonia', 'Serbia', 'Slovenia', 'Slovakia',
  'North Macedonia', 'Albania', 'Bosnia and Herzegovina', 'Montenegro', 'Kosovo',
  'Iceland', 'Luxembourg', 'Malta', 'Cyprus', 'Liechtenstein',
  'Monaco', 'Andorra', 'San Marino',
  'Pakistan', 'Bangladesh', 'Sri Lanka', 'Nepal', 'Myanmar',
  'Cambodia', 'Laos', 'Mongolia', 'Brunei', 'Maldives',
  'Morocco', 'Tunisia', 'Algeria', 'Libya', 'Ghana',
  'Cameroon', 'Senegal', 'Ivory Coast', 'Tanzania', 'Uganda',
  'Ethiopia', 'Rwanda', 'Mozambique', 'Zimbabwe', 'Botswana',
  'Namibia', 'Zambia', 'Malawi', 'Madagascar', 'Mauritius',
  'Angola', 'DR Congo', 'Somalia', 'Sudan', 'South Sudan',
  'Qatar', 'Kuwait', 'Bahrain', 'Oman', 'Jordan',
  'Lebanon', 'Iraq', 'Iran', 'Syria', 'Yemen',
  'Afghanistan', 'Palestine', 'Libya',
  'Fiji', 'Papua New Guinea', 'Samoa', 'Tonga',
];

const NEON = '#DCF995';
const SURFACE = 'rgba(255,255,255,0.06)';
const BORDER = 'rgba(255,255,255,0.1)';
const MUTED = 'rgba(255,255,255,0.4)';
const TEXT_WHITE = '#ffffff';

// ============================================================================
// Animations
// ============================================================================

const fadeInUp = keyframes`
  from { opacity: 0; transform: translateY(24px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const fadeOutUp = keyframes`
  from { opacity: 1; transform: translateY(0); }
  to   { opacity: 0; transform: translateY(-24px); }
`;

const errorPopIn = keyframes`
  0%   { opacity: 0; transform: translateY(8px) scale(0.95); }
  100% { opacity: 1; transform: translateY(0) scale(1); }
`;

// ============================================================================
// Types
// ============================================================================

/**
 * Wizard order. Terms are accepted BEFORE the account is created so the
 * acceptance timestamp can be stored with the org; verification is last.
 */
type StepType = 'name' | 'company' | 'industry' | 'country' | 'zip' | 'email' | 'tos' | 'password' | 'verify';

interface StepConfig {
  type: StepType;
  question: string;
  subtitle: string;
}

const BASE_STEPS: StepConfig[] = [
  { type: 'name', question: "What's your name?", subtitle: 'First and last name' },
  { type: 'company', question: "What's your company called?", subtitle: 'Your organization name' },
  { type: 'industry', question: 'What industry are you in?', subtitle: 'Type to search or enter your own' },
  { type: 'country', question: 'Where are you based?', subtitle: 'Type to search for your country' },
  { type: 'zip', question: '', subtitle: '' }, // question/subtitle set dynamically based on country
  { type: 'email', question: "What's your work email?", subtitle: "We'll send a verification code" },
  { type: 'tos', question: 'Do you agree to our Terms of Service and Privacy Policy?', subtitle: 'Type "yes" to agree' },
  { type: 'password', question: 'Create a password', subtitle: 'At least 8 characters with uppercase, lowercase, and numbers' },
  { type: 'verify', question: 'Check your email', subtitle: 'Enter the 6-digit verification code we sent you' },
];

const STEP_INDEX: Record<StepType, number> = BASE_STEPS.reduce((acc, s, i) => {
  acc[s.type] = i;
  return acc;
}, {} as Record<StepType, number>);

// ============================================================================
// Main Component
// ============================================================================

const STORAGE_KEY = 'logopulse_signup';

interface SavedState {
  fields: Record<string, string>;
  step: number;
  /** Set once POST /v1/register succeeded: the Cognito user + org exist. */
  registered?: boolean;
  tosAcceptedAt?: string;
}

function loadSavedState(): SavedState | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch { return null; }
}

function computeResumeStep(saved: SavedState): number {
  // An account already exists for this email: only verification remains.
  if (saved.registered) return STEP_INDEX.verify;

  const fields = saved.fields;
  // Walk through steps and find the first one that's incomplete.
  // Never auto-resume past the ToS step — the password is never persisted.
  const checks: Array<() => boolean> = [
    () => !!fields.firstName?.trim() && !!fields.lastName?.trim(), // name
    () => !!fields.company?.trim() && fields.company.trim().length >= 2, // company
    () => !!fields.industry?.trim(), // industry
    () => !!fields.country?.trim(), // country
    () => !!fields.zip?.trim(), // zip
    () => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(fields.email?.trim() || ''), // email
  ];
  for (let i = 0; i < checks.length; i++) {
    if (!checks[i]()) return i;
  }
  return STEP_INDEX.tos;
}

export const SignupPage: React.FC = () => {
  const navigate = useNavigate();
  const saved = useMemo(() => loadSavedState(), []);

  // Form data (restore from sessionStorage if available)
  const [firstName, setFirstName] = useState(saved?.fields.firstName || '');
  const [lastName, setLastName] = useState(saved?.fields.lastName || '');
  const [company, setCompany] = useState(saved?.fields.company || '');
  const [industry, setIndustry] = useState(saved?.fields.industry || '');
  const [country, setCountry] = useState(saved?.fields.country || '');
  const [zip, setZip] = useState(saved?.fields.zip || '');
  const [email, setEmail] = useState(saved?.fields.email || '');
  const [password, setPassword] = useState('');
  const [verifyCode, setVerifyCode] = useState('');
  const [tosAnswer, setTosAnswer] = useState('');
  const [tosAcceptedAt, setTosAcceptedAt] = useState<string | undefined>(saved?.tosAcceptedAt);
  // True once the org exists (registered here, or an UNCONFIRMED signup resumed from the email step).
  const [registered, setRegistered] = useState<boolean>(Boolean(saved?.registered));

  // UI state — resume at the right step
  const [step, setStep] = useState(() => saved ? computeResumeStep(saved) : 0);
  const [animating, setAnimating] = useState(false);
  const [animDir, setAnimDir] = useState<'in' | 'out'>('in');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [tosChecks, setTosChecks] = useState<[boolean, boolean]>([false, false]);
  const [resendCooldown, setResendCooldown] = useState(0);

  // Search state (value displayed in search input)
  const [searchQuery, setSearchQuery] = useState('');

  const isUS = country.trim().toLowerCase() === 'united states';
  const postalLabel = isUS ? 'zip code' : 'postal code';

  const STEPS = useMemo<StepConfig[]>(() =>
    BASE_STEPS.map(s =>
      s.type === 'zip'
        ? { ...s, question: `What's your ${postalLabel}?`, subtitle: isUS ? '5-digit zip code' : 'Your postal code' }
        : s
    ),
  [isUS, postalLabel]);

  const currentStep = STEPS[step];

  // Clear any stale Amplify auth state on mount
  useEffect(() => {
    auth.logout().catch(() => {});
  }, []);

  // Resend cooldown timer
  useEffect(() => {
    if (resendCooldown <= 0) return;
    const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
    return () => clearTimeout(timer);
  }, [resendCooldown]);

  // Persist form data to sessionStorage (never persist password)
  useEffect(() => {
    const state: SavedState = {
      fields: { firstName, lastName, company, industry, country, zip, email },
      step,
      registered,
      tosAcceptedAt,
    };
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  }, [firstName, lastName, company, industry, country, zip, email, step, registered, tosAcceptedAt]);

  // Filtered suggestions for industry/country
  const suggestions = useMemo(() => {
    if (currentStep.type === 'industry' && searchQuery.length > 0) {
      return INDUSTRIES.filter(i => i.toLowerCase().includes(searchQuery.toLowerCase())).slice(0, 3);
    }
    if (currentStep.type === 'country' && searchQuery.length > 0) {
      return COUNTRIES.filter(c => c.toLowerCase().includes(searchQuery.toLowerCase())).slice(0, 3);
    }
    return [];
  }, [currentStep.type, searchQuery]);

  // ---- Navigation ----

  const goToStep = useCallback((newStep: number) => {
    setError(null);
    setAnimDir('out');
    setAnimating(true);
    setTimeout(() => {
      setStep(newStep);
      setAnimDir('in');
      // Reset search state when entering search steps
      const nextType = STEPS[newStep].type;
      if (nextType === 'industry') setSearchQuery(industry);
      else if (nextType === 'country') setSearchQuery(country);
      else setSearchQuery('');
      // Start resend cooldown when entering verify step
      if (nextType === 'verify') setResendCooldown(60);
      setTimeout(() => setAnimating(false), 300);
    }, 200);
  }, [industry, country, STEPS]);

  const goBack = useCallback(() => {
    if (step <= 0) return;
    // Once the account exists there is nothing to go back to except verifying.
    if (registered) return;
    goToStep(step - 1);
  }, [step, goToStep, registered]);

  /** After verification: sign in with the in-memory password, or hand off to /login. */
  const completeSignup = useCallback(async () => {
    const normalizedEmail = email.trim().toLowerCase();
    sessionStorage.removeItem(STORAGE_KEY);
    if (!password) {
      navigate('/login?verified=1', { replace: true });
      return;
    }
    setLoading(true);
    try {
      const result = await auth.login(normalizedEmail, password);
      if (result.nextStep !== 'DONE') {
        navigate('/login?verified=1', { replace: true });
        return;
      }
      const token = await api.getAuthToken(true);
      if (token) {
        await useConfigStore.getState().loadConfig(true);
      }
      window.location.href = '/overview';
    } catch (err: any) {
      setLoading(false);
      navigate('/login?verified=1', { replace: true });
    }
  }, [email, password, navigate]);

  // ---- Validation & Advance ----

  const validateAndAdvance = useCallback(async () => {
    setError(null);

    switch (currentStep.type) {
      case 'name':
        if (!firstName.trim()) { setError('Please enter your first name'); return; }
        if (!lastName.trim()) { setError('Please enter your last name'); return; }
        break;
      case 'company':
        if (!company.trim() || company.trim().length < 2) { setError('Company name must be at least 2 characters'); return; }
        break;
      case 'industry':
        if (!industry.trim()) { setError('Please select or enter an industry'); return; }
        break;
      case 'country':
        if (!country.trim()) { setError('Please select or enter a country'); return; }
        break;
      case 'zip':
        if (!zip.trim()) { setError(`Please enter your ${postalLabel}`); return; }
        if (isUS && zip.trim().length < 4) { setError('Zip code must be 4–5 digits'); return; }
        break;
      case 'email': {
        const normalizedEmail = email.trim().toLowerCase();
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalizedEmail)) { setError('Please enter a valid email'); return; }
        // Check if email is already registered
        setLoading(true);
        try {
          const result = await api.checkEmail(normalizedEmail);
          setLoading(false);
          if (result.exists && result.status === 'CONFIRMED') {
            setError('An account with this email already exists. Please sign in instead.');
            return;
          }
          if (result.exists && result.status === 'UNCONFIRMED') {
            // Abandoned signup: the org already exists, only verification is missing.
            // Re-registering would fail with 409, so resend the code and jump to verify.
            try { await auth.resendSignUpCode(normalizedEmail); } catch { /* code may already be in flight */ }
            setRegistered(true);
            setPassword('');
            goToStep(STEP_INDEX.verify);
            return;
          }
          // non-existent — proceed with signup
        } catch {
          // If check fails, don't block — registration will catch duplicates
          setLoading(false);
        }
        break;
      }
      case 'tos': {
        if (tosAnswer.toLowerCase() !== 'yes') {
          if (tosAnswer.toLowerCase() === 'no') {
            setError('You must agree to the Terms of Service to continue');
          } else {
            setError('Type "yes" to agree');
          }
          return;
        }
        setTosAcceptedAt(new Date().toISOString());
        setTosChecks([true, false]);
        setTimeout(() => setTosChecks([true, true]), 400);
        setTimeout(() => goToStep(STEP_INDEX.password), 900);
        return;
      }
      case 'password': {
        if (password.length < 8) { setError('Password must be at least 8 characters'); return; }
        if (!tosAcceptedAt) { goToStep(STEP_INDEX.tos); return; }
        // Submit registration
        setLoading(true);
        try {
          await api.register({
            email: email.trim().toLowerCase(),
            password,
            orgName: company.trim(),
            firstName: firstName.trim(),
            lastName: lastName.trim(),
            industry: industry.trim(),
            country: country.trim(),
            zipCode: zip.trim(),
            tosAcceptedAt,
          });
          setRegistered(true);
          setLoading(false);
          goToStep(STEP_INDEX.verify);
        } catch (err: any) {
          setLoading(false);
          const status = err.response?.status;
          const msg = err.response?.data?.error || err.message || 'Registration failed';
          if (status === 409) {
            setError('An account with this email already exists. Please sign in instead.');
          } else {
            setError(msg);
          }
        }
        return;
      }
      case 'verify': {
        if (verifyCode.length !== 6) { setError('Please enter the 6-digit code'); return; }
        setLoading(true);
        try {
          await auth.confirmSignUp(email.trim().toLowerCase(), verifyCode);
          setLoading(false);
          await completeSignup();
        } catch (err: any) {
          // If user is already confirmed, just finish
          const errMsg = err.message || '';
          if (errMsg.includes('Current status is CONFIRMED') || errMsg.includes('cannot be confirmed')) {
            setLoading(false);
            await completeSignup();
            return;
          }
          setLoading(false);
          if (err?.name === 'CodeMismatchException') setError('That code is not correct');
          else if (err?.name === 'ExpiredCodeException') setError('That code has expired. Resend a new one.');
          else setError(err.message || 'Invalid verification code');
        }
        return;
      }
    }

    // Default: advance to next step
    goToStep(step + 1);
  }, [currentStep.type, firstName, lastName, company, industry, country, zip, email, password, verifyCode, tosAnswer, tosAcceptedAt, step, goToStep, completeSignup, isUS, postalLabel]);

  const handleResendCode = useCallback(async () => {
    if (resendCooldown > 0) return;
    try {
      await auth.resendSignUpCode(email.trim().toLowerCase());
      setResendCooldown(60);
    } catch (err: any) {
      setError(err.message || 'Failed to resend code');
    }
  }, [email, resendCooldown]);

  // ---- Search helpers ----

  const handleSearchChange = useCallback((value: string) => {
    setSearchQuery(value);
    if (currentStep.type === 'industry') setIndustry(value);
    else if (currentStep.type === 'country') setCountry(value);
  }, [currentStep.type]);

  const handleSuggestionSelect = useCallback((value: string) => {
    if (currentStep.type === 'industry') setIndustry(value);
    else if (currentStep.type === 'country') setCountry(value);
    setSearchQuery(value);
  }, [currentStep.type]);

  // ---- Input hint text ----

  const inputHint = useMemo(() => {
    switch (currentStep.type) {
      case 'name': return 'Enter to continue';
      case 'industry':
      case 'country': return '↑↓ navigate · Enter to select';
      case 'zip': return isUS ? '5 digits' : 'Enter to continue';
      case 'password': return '8+ characters';
      case 'verify': return '6-digit code';
      case 'tos': return 'Type "yes" to agree';
      default: return 'Enter to continue';
    }
  }, [currentStep.type, isUS]);

  // ---- SmartInput mode/props derivation ----

  const getSmartInputProps = useCallback(() => {
    const placeholders: Record<string, string> = {
      company: 'Acme Corp',
      zip: isUS ? '10001' : 'e.g. SW1A 1AA',
      email: 'you@company.com',
      industry: 'e.g. SaaS',
      country: 'e.g. United States',
    };

    const stepType = currentStep.type;

    switch (stepType) {
      case 'name':
        return {
          mode: 'name' as SmartInputMode,
          value: firstName,
          onChange: setFirstName,
          secondaryValue: lastName,
          onSecondaryChange: setLastName,
          placeholder: 'Your name',
        };
      case 'industry':
      case 'country':
        return {
          mode: 'search' as SmartInputMode,
          value: searchQuery,
          onChange: handleSearchChange,
          suggestions,
          onSuggestionSelect: handleSuggestionSelect,
          placeholder: placeholders[stepType],
        };
      case 'password':
        return {
          mode: 'password' as SmartInputMode,
          value: password,
          onChange: setPassword,
          placeholder: 'Enter password',
        };
      case 'verify':
        return {
          mode: 'code' as SmartInputMode,
          value: verifyCode,
          onChange: setVerifyCode,
        };
      case 'tos':
        return {
          mode: 'tos' as SmartInputMode,
          value: tosAnswer,
          onChange: setTosAnswer,
          checkmarks: tosChecks as [boolean, boolean],
          placeholder: 'Type "yes" or "no"',
        };
      default: {
        const values: Record<string, string> = { company, zip, email };
        const handleZipChange = (v: string) => {
          if (isUS) {
            const digits = v.replace(/\D/g, '').slice(0, 5);
            setZip(digits);
          } else {
            setZip(v);
          }
        };
        const setters: Record<string, (v: string) => void> = {
          company: setCompany,
          zip: handleZipChange,
          email: setEmail,
        };
        const inputTypes: Record<string, string> = { email: 'email' };
        if (stepType === 'zip' && isUS) inputTypes.zip = 'tel';
        return {
          mode: 'text' as SmartInputMode,
          value: values[stepType] || '',
          onChange: setters[stepType] || (() => {}),
          placeholder: placeholders[stepType] || '',
          inputType: inputTypes[stepType],
        };
      }
    }
  }, [currentStep.type, firstName, lastName, searchQuery, handleSearchChange, suggestions, handleSuggestionSelect, password, verifyCode, tosAnswer, tosChecks, company, zip, email, isUS]);

  return (
    <Box
      sx={{
        minHeight: '100vh',
        width: '100%',
        background: 'linear-gradient(135deg, #1a1a2e 0%, #0f0a1a 50%, #1B0826 100%)',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Top bar */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: { xs: 2, sm: 4 }, pt: 3 }}>
        {/* Logo + back */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {step > 0 && (
            <Box
              onClick={goBack}
              sx={{
                cursor: 'pointer', color: MUTED, display: 'flex', alignItems: 'center', transition: 'color 0.2s',
                '&:hover': { color: TEXT_WHITE },
              }}
            >
              <ArrowBackIcon sx={{ fontSize: 22 }} />
            </Box>
          )}
          <img src={logoSvg} alt="LogoPulse" style={{ height: 32, width: 'auto' }} />
        </Box>

        {/* Step indicator */}
        <Typography sx={{ fontSize: '13px', color: NEON, fontWeight: 600, letterSpacing: '0.5px' }}>
          {step + 1} / {STEPS.length}
        </Typography>
      </Box>

      {/* Question area — fills the vertical space, centers the question */}
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', px: { xs: 2, sm: 4 } }}>
        <Box
          key={step}
          sx={{
            textAlign: 'center',
            width: '100%',
            maxWidth: '780px',
            animation: animating
              ? (animDir === 'out' ? `${fadeOutUp} 0.2s ease-out forwards` : `${fadeInUp} 0.3s ease-out forwards`)
              : `${fadeInUp} 0.3s ease-out forwards`,
          }}
        >
          <Typography sx={{ fontSize: { xs: '36px', sm: '52px', md: '60px' }, fontWeight: 700, color: TEXT_WHITE, mb: 1.5, lineHeight: 1.1 }}>
            {currentStep.question}
          </Typography>
          <Typography sx={{ fontSize: { xs: '16px', sm: '18px' }, color: MUTED }}>
            {currentStep.subtitle}
          </Typography>
        </Box>
      </Box>

      {/* Bottom input area — pinned to the bottom */}
      <Box sx={{ px: { xs: 2, sm: 4 }, pb: { xs: 3, sm: 5 }, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        {/* Error chip — above input */}
        <Box sx={{ height: 40, display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 3 }}>
          {error && (
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 1,
                px: 2,
                py: 0.75,
                borderRadius: '12px',
                bgcolor: 'rgba(239,68,68,0.1)',
                border: '1px solid rgba(239,68,68,0.2)',
                animation: `${errorPopIn} 0.25s ease-out`,
              }}
            >
              <Typography sx={{ fontSize: '13px', color: 'rgba(239,68,68,0.9)', fontWeight: 500 }}>
                {error}
              </Typography>
              <Box
                onClick={() => setError(null)}
                sx={{
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  color: 'rgba(239,68,68,0.5)',
                  transition: 'color 0.15s',
                  '&:hover': { color: 'rgba(239,68,68,0.9)' },
                }}
              >
                <CloseIcon sx={{ fontSize: 16 }} />
              </Box>
            </Box>
          )}
        </Box>

        {/* Smart input widget + continue button */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%', maxWidth: '780px' }}>
          {/* Input box with recessed neon glow */}
          <Box
            sx={{
              height: '100px',
              flex: 1,
              borderRadius: '20px',
              bgcolor: SURFACE,
              border: '1px solid',
              borderColor: BORDER,
              display: 'flex',
              alignItems: 'center',
              overflow: 'hidden',
              position: 'relative',
              transition: 'border-color 0.3s, box-shadow 0.4s',
              '&:focus-within': {
                borderColor: 'rgba(220,249,149,0.25)',
                boxShadow: `
                  0 0 15px 2px rgba(220,249,149,0.08),
                  0 0 40px 8px rgba(220,249,149,0.06),
                  0 0 80px 20px rgba(220,249,149,0.04),
                  inset 0 0 30px 4px rgba(220,249,149,0.03)
                `,
              },
            }}
          >
            {/* Hint label — top right corner */}
            <Typography
              sx={{
                position: 'absolute',
                top: 8,
                right: 14,
                fontSize: '11px',
                color: 'rgba(255,255,255,0.2)',
                pointerEvents: 'none',
                zIndex: 1,
                letterSpacing: '0.3px',
              }}
            >
              {inputHint}
            </Typography>
            <SmartInput {...getSmartInputProps()} onSubmit={validateAndAdvance} />
          </Box>

          {/* Continue button */}
          <Box
            onClick={loading ? undefined : validateAndAdvance}
            sx={{
              width: 56,
              height: 56,
              borderRadius: '50%',
              bgcolor: loading ? 'rgba(220,249,149,0.4)' : NEON,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: loading ? 'default' : 'pointer',
              flexShrink: 0,
              transition: 'all 0.2s',
              '&:hover': loading ? {} : {
                transform: 'scale(1.08)',
                boxShadow: `0 0 20px rgba(220,249,149,0.3)`,
              },
              '&:active': loading ? {} : {
                transform: 'scale(0.95)',
              },
            }}
          >
            {loading ? (
              <CircularProgress size={24} sx={{ color: '#1B0826' }} />
            ) : (
              <ArrowForwardIcon sx={{ color: '#1B0826', fontSize: 26 }} />
            )}
          </Box>
        </Box>

        {/* Resend code link (verify step) */}
        {currentStep.type === 'verify' && (
          <Box sx={{ mt: 2, textAlign: 'center' }}>
            <Typography
              onClick={handleResendCode}
              sx={{
                fontSize: '14px',
                color: resendCooldown > 0 ? MUTED : NEON,
                cursor: resendCooldown > 0 ? 'default' : 'pointer',
                '&:hover': resendCooldown > 0 ? {} : { textDecoration: 'underline' },
              }}
            >
              {resendCooldown > 0 ? `Resend code in ${resendCooldown}s` : 'Resend code'}
            </Typography>
          </Box>
        )}

        {/* ToS links (tos step) */}
        {currentStep.type === 'tos' && (
          <Box sx={{ mt: 2, textAlign: 'center' }}>
            <Typography sx={{ fontSize: '13px', color: MUTED }}>
              <a href="https://logopulse.io/terms" target="_blank" rel="noopener noreferrer" style={{ color: 'rgba(255,255,255,0.6)', textDecoration: 'underline' }}>
                Terms of Service
              </a>
              {' and '}
              <a href="https://logopulse.io/privacy" target="_blank" rel="noopener noreferrer" style={{ color: 'rgba(255,255,255,0.6)', textDecoration: 'underline' }}>
                Privacy Policy
              </a>
            </Typography>
          </Box>
        )}

        {/* Sign in link */}
        <Box sx={{ textAlign: 'center', mt: 2 }}>
          <Typography sx={{ fontSize: '14px', color: MUTED }}>
            Already have an account?{' '}
            <span
              onClick={() => navigate('/login')}
              style={{ color: NEON, cursor: 'pointer', fontWeight: 600 }}
            >
              Sign in
            </span>
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};
